<script setup lang="ts">
/**
 * Inline interactive building block for the `render_radio_group` tool: a
 * single-choice question collected in-chat. Built on the host shadcn
 * `RadioGroup` primitive (resolved via `@/components/ui/*` so `src/chat` stays
 * free of reka-ui/Vue duplication). Emits the selected option id; the dispatcher
 * (`block-part.vue`) wraps it into the tool's result shape and submits it.
 */
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";

interface BlockOption {
  id: string;
  name: string;
  value?: number;
  description?: string;
}

const props = withDefaults(
  defineProps<{
    input: Record<string, unknown>;
    state: string;
    disabled?: boolean;
  }>(),
  { disabled: false },
);

const emit = defineEmits<{ (e: "submit", value: string): void }>();

const { t } = useI18n({ useScope: "local" });

const title = computed(() => (typeof props.input.title === "string" ? props.input.title : ""));
const options = computed<BlockOption[]>(() =>
  Array.isArray(props.input.options) ? (props.input.options as BlockOption[]) : [],
);

const resolved = computed(
  () =>
    props.disabled ||
    props.state === "output-available" ||
    props.state === "approval-responded" ||
    props.state === "output-error" ||
    props.state === "output-denied",
);

const selected = ref<string>("");
const submitted = ref(false);
const readOnly = computed(() => resolved.value || submitted.value);

function submit() {
  if (readOnly.value || !selected.value) {
    return;
  }
  submitted.value = true;
  emit("submit", selected.value);
}

const fieldId = computed(() =>
  typeof props.input.id === "string" ? props.input.id : "radio-group",
);
</script>

<template>
  <div class="not-prose space-y-3 rounded-xl border border-border bg-card p-4" data-slot="chat-block-radio">
    <p v-if="title" class="text-sm font-medium text-foreground">{{ title }}</p>

    <RadioGroup v-model="selected" :disabled="readOnly" :aria-label="title || fieldId">
      <label
        v-for="option in options"
        :key="option.id"
        :class="[
          'flex cursor-pointer items-start gap-3 rounded-lg border border-border p-3 transition-colors',
          selected === option.id ? 'border-primary bg-primary/5' : 'hover:bg-muted/50',
          readOnly ? 'cursor-default opacity-90' : '',
        ]"
      >
        <RadioGroupItem :value="option.id" :id="`${fieldId}-${option.id}`" class="mt-0.5" />
        <span class="flex min-w-0 flex-col">
          <Label :for="`${fieldId}-${option.id}`" class="cursor-[inherit] text-sm font-medium text-foreground">
            {{ option.name }}
          </Label>
          <span v-if="option.description" class="text-xs text-muted-foreground">
            {{ option.description }}
          </span>
        </span>
      </label>
    </RadioGroup>

    <div v-if="!resolved" class="flex justify-end">
      <Button type="button" size="sm" :disabled="!selected || submitted" @click="submit">
        {{ t("submit") }}
      </Button>
    </div>
  </div>
</template>

<i18n lang="yaml">
en:
  submit: Submit
</i18n>
